import { isEqual } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Analytics from 'analytics-opencare';
import { colors } from 'common/styles/style-constants';
import {
  createMatchAndResults,
  recreateMatchAndResults,
} from 'connect/actions';
import { ANONYMOUS_SESSION_KEYS } from 'connect/saga/handlers/anonymous-session/constants';
import { UpdateAnonymousSessionData } from 'connect/saga/handlers/anonymous-session/update-anonymous-session-data';
import {
  selectDataLoadingMatch,
  selectDataMatch,
  selectDataMatchToken,
} from 'connect/selectors';
import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useOfferService } from 'services/OfferService';

import { ReviewCarousel } from '../../../../common/components/ReviewCarousel/ReviewCarousel';
import { RESULTS_PATH } from '../../../../containers/App/Routes';
import OpencareLogoSvg from '../../../common/components/svgs/OpencareLogoSvg';
import { useFunnelStorage } from '../../storage/Storage';
import { generateStepAnalytics, isMatchFresh } from '../../utils';

import { LoadingMatch } from './components/LoadingMatch';
import { getMatchResultsPageConfiguration } from './constants';
import { LoadingSection, Main } from './styled-components';

const analytics = new Analytics();

const MINIMUM_LOAD_DURATION = 5000;

const Search = ({ setGeneratingMatch, funnelConfiguration }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const storage = useFunnelStorage();
  const { layout } = useWindowDimensions();

  const analyticsService = useAnalyticsService();
  const offerService = useOfferService();

  const [isAnimating, setIsAnimating] = useState(true);
  const [resultsUrl, setResultsUrl] = useState(RESULTS_PATH);

  const loadingMatch = useSelector(selectDataLoadingMatch);
  const savedMatch = useSelector(selectDataMatch);
  const savedMatchToken = useSelector(selectDataMatchToken);

  const savedMatchAge = moment().diff(moment(savedMatch.createdAt), 'minutes');
  const freshMatchExists = isMatchFresh(savedMatch);

  const creatingResults = isAnimating || loadingMatch;
  const match = !freshMatchExists ? storage.transform() : {};
  const locality = !freshMatchExists
    ? match?.location?.address?.locality
    : savedMatch.location?.address?.locality;

  useEffect(() => {
    if (creatingResults) {
      return;
    }

    // Clear clicked clinic IDs when we go to the results page
    const key = ANONYMOUS_SESSION_KEYS.CLICKED_MATCH_CLINIC_IDS;
    dispatch(
      UpdateAnonymousSessionData.createAction({
        [key]: [],
      }),
    );

    history.push(resultsUrl);
  }, [creatingResults]);

  useEffect(() => {
    // Let the navigation know we're working on it
    setGeneratingMatch(true);

    analytics.trackPageView({
      pageType: analytics.pageTypes.WAITING,
      pageName: 'funnel_waiting',
      // We don't pass position to Search, so we pass null here
      ...generateStepAnalytics(null, storage),
      ...analyticsService.analyticsData,
    });

    if (freshMatchExists) {
      // Refresh match with existing match ID and token
      dispatch(recreateMatchAndResults(savedMatch.id, savedMatchToken));
    } else if (
      Number.isNaN(savedMatchAge)
      || savedMatchAge > 5
      || !isEqual(match, savedMatch)
    ) {
      // Generate new match from funnel data if:
      // 1. Saved match does not exist
      // 2. Saved match exists and is older than 5 minutes
      // 3. New match is different than saved match
      dispatch(
        createMatchAndResults(
          match,
          funnelConfiguration,
          getMatchResultsPageConfiguration(),
        ),
      );
      setResultsUrl(`${resultsUrl}?firstView`);
    }

    // On unmount, inform Navigation we're done
    return () => {
      setGeneratingMatch(false);
    };
  }, []);

  useEffect(() => {
    const animation = setTimeout(() => {
      setIsAnimating(false);
    }, MINIMUM_LOAD_DURATION);

    return () => clearTimeout(animation);
  }, []);

  const progress = storage.progress ?? {};
  const insuranceProvider = progress.insurance?.data?.provider;
  const selectedTreatments = progress.reason?.data?.treatments?.map(
    treatment => treatment.title,
  );

  return (
    <Main
      id="step-search"
      data-testid="step-search"
      height={offerService.hasActiveOffer ? 'calc(100vh - 60px)' : '100vh'}
    >
      <LoadingSection id="loading-section">
        {layout !== DIMENSIONS.MOBILE 
          && <OpencareLogoSvg color={colors.darkBlue} margin="0 0 20px 0" />
        }
        <LoadingMatch
          locality={locality}
          insuranceProvider={insuranceProvider}
          selectedTreatments={selectedTreatments}
          minimumLoadDuration={MINIMUM_LOAD_DURATION}
        />
      </LoadingSection>
      <ReviewCarousel />
    </Main>
  );
};

Search.propTypes = {
  setGeneratingMatch: PropTypes.func.isRequired,
  funnelConfiguration: PropTypes.object.isRequired,
};

export default Search;
